import { Globe } from 'lucide-react'
import { cn } from '../lib/cn'

export type LanguageOption = {
  code: string
  /** What the language calls itself. */
  label: string
  /** 0–1. Below 1, the option says so rather than pretending. */
  coverage: number
}

/**
 * The language control, shared by all three apps.
 *
 * <p>One component because the choice is the same choice everywhere: the
 * back-office had none at all, the provider app had none, and the client app
 * had a screen of its own whose list of languages had drifted from what the
 * server accepts.
 *
 * <p>Coverage is shown, not hidden. Fon and Bariba are offered because the
 * platform is for Benin and the spec names them, but their bundles are barely
 * started — a person picking one and getting French with no explanation would
 * reasonably think the feature is broken, which is exactly what it looked like
 * before. Saying "partial" is the honest version of the same screen.
 *
 * <p>Presentational: it renders options and reports a choice. Persisting to the
 * device and to the account is the caller's, because only the caller knows
 * whether there is an account to persist to — the back-office sign-in screen
 * has none.
 */
export function LanguageSwitcher({
  options,
  current,
  onSelect,
  partialLabel = 'Traduction partielle',
  className,
  compact = false,
}: {
  options: readonly LanguageOption[]
  current: string
  onSelect: (code: string) => void
  partialLabel?: string
  className?: string
  compact?: boolean
}) {
  return (
    <ul className={cn('flex flex-col gap-2', className)}>
      {options.map((option) => {
        const selected = option.code === current
        return (
          <li key={option.code}>
            <button
              type="button"
              onClick={() => onSelect(option.code)}
              aria-pressed={selected}
              lang={option.code}
              className={cn(
                'flex w-full items-center gap-3 rounded-lg border px-4 text-left text-sm',
                compact ? 'py-2' : 'py-3',
                selected
                  ? 'border-brand-600 bg-brand-50 text-brand-700'
                  : 'border-slate-200 text-slate-700',
              )}
            >
              <Globe
                className={cn('size-4 shrink-0', selected ? 'text-brand-600' : 'text-slate-400')}
                aria-hidden="true"
              />
              <span className="flex-1 truncate font-medium">{option.label}</span>
              {option.coverage < 1 ? (
                // Not a warning colour: an incomplete translation is a fact
                // about the language, not a mistake by the person choosing it.
                <span className="text-2xs shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-slate-500">
                  {partialLabel}
                </span>
              ) : null}
            </button>
          </li>
        )
      })}
    </ul>
  )
}
